/**
 * Agent chat "surface" — which search experience a given route belongs
 * to. The agent chat frontend (list page, new-chat page, and the whole
 * detail tree under app/agent/[id]/) is otherwise agentType-agnostic:
 * it operates on a chatId, and the agentType is only chosen at chat
 * CREATION. This lets ONE set of components serve both:
 *
 *   - v1 search   → /searches, /search/new, /search/:chatId   (agentType 'search')
 *   - search v2   → /searches-v2, /search-v2/new, /search-v2/:chatId (agentType 'search_v2')
 *
 * Same styling, same attachments/permissions/artifacts — the only
 * per-surface differences (which agent runs, where links point, the
 * list title) are resolved from the current pathname here. v1 behaviour
 * is preserved exactly: anything not under the v2 paths gets SEARCH_V1.
 */

export type AgentSurface = {
    /**
     * agentType passed to chat creation + list filtering. `undefined`
     * means "don't send one" → the server defaults to 'search', which
     * is byte-identical to v1's original create()/list() calls.
     */
    agentType?: string;
    /** List route, e.g. '/searches'. */
    listPath: string;
    /** New-chat route, e.g. '/search/new'. */
    newPath: string;
    /** Detail route base; detail = `${detailBase}/${chatId}`. */
    detailBase: string;
    listTitle: string;
    listSubtitle: string;
    /**
     * Force assistant text to render with no reveal animation (the
     * 'instant' streaming style), ignoring the user's global
     * streaming-style preference. v2 opts in for the fastest possible
     * render; v1 keeps the chosen animation.
     */
    forceInstantStream?: boolean;
};

const SEARCH_V1: AgentSurface = {
    agentType: undefined,
    listPath: '/searches',
    newPath: '/search/new',
    detailBase: '/search',
    listTitle: 'Searches',
    listSubtitle: 'Your saved searches and active conversations.',
};

const SEARCH_V2: AgentSurface = {
    agentType: 'search_v2',
    listPath: '/searches-v2',
    newPath: '/search-v2/new',
    detailBase: '/search-v2',
    listTitle: 'Searches v2',
    listSubtitle: 'A fresh search experience.',
    // v2 renders assistant text instantly — no reveal animation — for the
    // fastest possible render. v1 keeps the user's chosen streaming style.
    forceInstantStream: true,
};

const SEARCH_V3: AgentSurface = {
    agentType: 'search_v3',
    listPath: '/searches-v3',
    newPath: '/search-v3/new',
    detailBase: '/search-v3',
    listTitle: 'Searches',
    listSubtitle: 'Ranked candidate search.',
    forceInstantStream: true,
};

export function agentSurfaceForPath(pathname: string): AgentSurface {
    if (
        pathname.startsWith('/searches-v3') ||
        pathname.startsWith('/search-v3')
    ) {
        return SEARCH_V3;
    }
    if (
        pathname.startsWith('/searches-v2') ||
        pathname.startsWith('/search-v2')
    ) {
        return SEARCH_V2;
    }
    return SEARCH_V1;
}
